class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.resetButton = this.querySelector('[data-ts-search-reset]');
    this.resetButton?.addEventListener('click', this.onResetButtonClick.bind(this));
    this.searchButton = this.querySelector('.search__button');
    this.searchButton?.addEventListener('click', this.onSearchButtonClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  static normalizeSearchText(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\u0400-\u04ff]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  static getBrandSearchAliases() {
    return [
      { query: 'tissot', aliases: ['tissot', '\u0442\u0438\u0441\u043e\u0442', '\u0442\u0438\u0441\u043e'] },
      { query: 'casio', aliases: ['casio', '\u043a\u0430\u0441\u0438\u043e'] },
      {
        query: 'jacques du manoir',
        aliases: [
          'jacques du manoir',
          '\u0436\u0430\u043a \u0434\u044e \u043c\u0430\u043d\u043e\u0430\u0440',
          '\u0436\u0430\u043a \u0434\u0443 \u043c\u0430\u043d\u043e\u0430\u0440',
          '\u0436\u0430\u043a \u0434\u044c\u043e \u043c\u0430\u043d\u043e\u0430\u0440',
          '\u0436\u0430\u043a \u043c\u0430\u043d\u043e\u0430\u0440',
        ],
      },
      {
        query: 'jdm military',
        aliases: [
          'jdm military',
          '\u0434\u0436\u0435\u0439 \u0434\u0438 \u0435\u043c \u043c\u0438\u043b\u0438\u0442\u0430\u0440\u0438',
          '\u0434\u0436\u0435\u0439\u0434\u0438\u0435\u043c \u043c\u0438\u043b\u0438\u0442\u0430\u0440\u0438',
          '\u043c\u0438\u043b\u0438\u0442\u0430\u0440\u0438',
        ],
      },
      { query: 'seiko', aliases: ['seiko', '\u0441\u0435\u0439\u043a\u043e'] },
      { query: 'invicta', aliases: ['invicta', '\u0438\u043d\u0432\u0438\u043a\u0442\u0430'] },
      { query: 'orient', aliases: ['orient', '\u043e\u0440\u0438\u0435\u043d\u0442'] },
      { query: 'citizen', aliases: ['citizen', '\u0441\u0438\u0442\u0438\u0437\u0435\u043d'] },
      { query: 'zeppelin', aliases: ['zeppelin', '\u0446\u0435\u043f\u0435\u043b\u0438\u043d', '\u0437\u0435\u043f\u0435\u043b\u0438\u043d'] },
      { query: 'bulova', aliases: ['bulova', '\u0431\u0443\u043b\u043e\u0432\u0430'] },
      { query: 'frederique constant', aliases: ['frederique constant', '\u0444\u0440\u0435\u0434\u0435\u0440\u0438\u043a \u043a\u043e\u043d\u0441\u0442\u0430\u043d\u0442'] },
      { query: 'festina', aliases: ['festina', '\u0444\u0435\u0441\u0442\u0438\u043d\u0430'] },
      { query: 'philipp plein', aliases: ['philipp plein', '\u0444\u0438\u043b\u0438\u043f \u043f\u043b\u0435\u0439\u043d'] },
      { query: 'tsar bomba', aliases: ['tsar bomba', '\u0446\u0430\u0440 \u0431\u043e\u043c\u0431\u0430'] },
    ];
  }

  static resolveSearchTerm(searchTerm) {
    const normalizedTerm = PredictiveSearch.normalizeSearchText(searchTerm);
    if (normalizedTerm.length < 3) return searchTerm;

    const compactTerm = normalizedTerm.replace(/\s+/g, '');
    const match = PredictiveSearch.getBrandSearchAliases().find((brand) => {
      return brand.aliases.some((alias) => {
        const normalizedAlias = PredictiveSearch.normalizeSearchText(alias);
        const compactAlias = normalizedAlias.replace(/\s+/g, '');

        return (
          normalizedAlias === normalizedTerm ||
          compactAlias === compactTerm ||
          normalizedAlias.startsWith(normalizedTerm) ||
          compactAlias.startsWith(compactTerm) ||
          normalizedTerm.includes(normalizedAlias)
        );
      });
    });

    return match?.query || searchTerm;
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();
    this.resetInlineSearch();
  }

  onResetButtonClick(event) {
    event.preventDefault();
    this.resetInlineSearch();
  }

  onSearchButtonClick(event) {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const form = this.input.form;
    const searchUrl = new URL(form?.action || routes.search_url, window.location.origin);
    const resolvedSearchTerm = PredictiveSearch.resolveSearchTerm(searchTerm);
    searchUrl.searchParams.set(this.input.name || 'q', resolvedSearchTerm);

    form?.querySelectorAll('input[type="hidden"][name]').forEach((input) => {
      if (input.name !== this.input.name) {
        searchUrl.searchParams.set(input.name, input.value);
      }
    });

    this.close();
    window.location.href = searchUrl.toString();
  }

  ensureInlineTypingElement() {
    const field = this.input.closest('.ts-inline-search__field');
    if (!field) return null;

    let typing = field.querySelector('.ts-inline-search__typing');
    let typingText = typing?.querySelector('.ts-inline-search__typing-text');

    if (!typing) {
      typing = document.createElement('span');
      typing.className = 'ts-inline-search__typing';
      typing.setAttribute('aria-hidden', 'true');
      field.insertBefore(typing, this.resetButton || field.querySelector('.search__button'));
    }

    if (!typingText) {
      typingText = document.createElement('span');
      typingText.className = 'ts-inline-search__typing-text';
      typing.appendChild(typingText);
    }

    typingText.textContent = this.input.getAttribute('placeholder') || '';
    field.classList.add('ts-inline-search__field--typing');

    return typingText;
  }

  restartInlineTypingAnimation() {
    const typingText = this.ensureInlineTypingElement();
    if (!typingText) return;

    typingText.style.animation = 'none';
    typingText.offsetWidth;
    typingText.style.animation = '';
  }

  resetInlineSearch() {
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
    this.input.value = '';
    this.resetButton?.classList.add('hidden');
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.restartInlineTypingAnimation();

    window.requestAnimationFrame(() => {
      this.input.blur();
      this.resetButton?.blur();
    });
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const resolvedSearchTerm = PredictiveSearch.resolveSearchTerm(searchTerm);
    const queryKey = resolvedSearchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(resolvedSearchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  syncSearchForTermText(searchTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (!searchForTextElement || !searchTerm) return;

    const currentText = searchForTextElement.textContent || '';
    const quoteStart = currentText.indexOf('\u201e');
    const quoteEnd = currentText.lastIndexOf('\u201c');

    if (quoteStart !== -1 && quoteEnd > quoteStart) {
      searchForTextElement.textContent = `${currentText.slice(0, quoteStart + 1)}${searchTerm}${currentText.slice(quoteEnd)}`;
    }
  }

  renderSearchResults(resultsMarkup, displaySearchTerm = this.searchTerm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.syncSearchForTermText(displaySearchTerm);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
